import { useMemo, useState, type ReactElement } from 'react';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconChevronDown, IconDownload } from '../components/icons';
import Pagination from '../components/Pagination';
import { labelFor, useConfig } from '../context/ConfigContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface PayslipLine {
  id: number;
  type: 'addition' | 'deduction';
  category: string;
  amount: number;
}

interface Payslip {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  period_start: string;
  period_end: string;
  base_salary: number;
  total_additions: number;
  total_deductions: number;
  gross_pay: number;
  net_pay: number;
  lines: PayslipLine[];
}

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

export function Payslips() {
  const { lists, settings } = useConfig();
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const query = useMemo(
    () => ({ search: search || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined }),
    [search, dateFrom, dateTo]
  );
  const { data, loading, error, setPage } = usePaginatedList<Payslip>('/payslips', query);

  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const categoryLabel = (line: PayslipLine) =>
    labelFor(lists, line.type === 'addition' ? 'payroll_addition_type' : 'payroll_deduction_type', line.category);

  const downloadPdf = (slip: Payslip) => {
    const filename = `Payslip-${slip.user_name.replace(/\s+/g, '-')}-${slip.period_start.slice(0, 7)}.pdf`;
    downloadFile(api, `/payslips/${slip.id}/pdf`, filename);
  };

  return (
    <>
      <header className="page-header">
        <h1>Payslips</h1>
      </header>

      <div className="toolbar">
        <input type="search" placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="checkbox-inline">
          Date from
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Period</th>
                <th>Gross</th>
                <th>Deductions</th>
                <th>Net</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.flatMap((slip) => {
                  const isExpanded = expanded.has(slip.id);
                  const rows = [
                    <tr key={slip.id}>
                      <td>
                        <button
                          type="button"
                          className="btn icon-btn btn-ghost"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          onClick={() => toggleExpanded(slip.id)}
                        >
                          <IconChevronDown
                            className="icon-btn-svg"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                          />
                        </button>
                      </td>
                      <td className="muted">{slip.employee_code ?? '—'}</td>
                      <td>
                        <strong>{slip.user_name}</strong>
                      </td>
                      <td>{formatPeriod(slip.period_start, slip.period_end)}</td>
                      <td>{fmtMoney(slip.gross_pay)}</td>
                      <td>{fmtMoney(slip.total_deductions)}</td>
                      <td>
                        <strong>{fmtMoney(slip.net_pay)}</strong>
                      </td>
                      <td className="right">
                        <IconButton icon={IconDownload} label="Download PDF" onClick={() => downloadPdf(slip)} />
                      </td>
                    </tr>,
                  ];

                  if (!isExpanded) return rows;

                  const detailRows: ReactElement[] = [
                    <tr key={`base-${slip.id}`}>
                      <td></td>
                      <td colSpan={5} className="muted">
                        Base salary
                      </td>
                      <td>{fmtMoney(slip.base_salary)}</td>
                      <td></td>
                    </tr>,
                    ...slip.lines.map((line) => (
                      <tr key={`line-${line.id}`}>
                        <td></td>
                        <td colSpan={5} className="muted">
                          {line.type === 'addition' ? '+ ' : '− '}
                          {categoryLabel(line)}
                        </td>
                        <td>{fmtMoney(line.amount)}</td>
                        <td></td>
                      </tr>
                    )),
                  ];

                  return [...rows, ...detailRows];
                })
              ) : (
                <tr>
                  <td colSpan={8} className="muted center">
                    No payslips yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
